import { ArrowRightIcon } from 'lucide-react'
import React from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useGetVisionPlans } from 'components/hooks/member_onboarding_v2/useGetVisionPlans'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'
import { usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment } from 'utils/web_api'

import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { PageErrorBanner } from '../components/PageErrorBanner'
import VisionPlanCard from '../components/VisionPlanCard'
import { useOnboarding } from '../OnboardingContext'
import { type DentalPlan, type StepComponentProps, type VisionPlan } from '../types'

export function VisionPlansPage ({
  navigateToNextStep,
  navigateToPreviousStep,
}: StepComponentProps) {
  const { onboardingData, deleteEnrollment, planChangeSummary } = useOnboarding()
  const { enrollmentEventId } = useOnboarding()
  const [submitError, setSubmitError] =
    React.useState<GrapeAPIErrorType | null>(null)
  const {
    data: visionPlanData,
    isLoading,
    error,
  } = useGetVisionPlans({ enrollmentEventId })
  const { breakpointIsAtLeast } = useBreakpoint()
  const availablePlans = visionPlanData?.data ?? []
  const currentPlan =
    onboardingData?.enrollments?.find((e) => e.covers_vision) || null
  const renewalPlanCode = planChangeSummary?.next_plan_year.vision_plan?.plan_code || null
  let renewalPlan = null
  if (renewalPlanCode) {
    renewalPlan = availablePlans.find((plan) => plan.plan_code === renewalPlanCode)
  }

  const createEnrollment = useCreateEnrollment({
    mutation: {
      onSuccess: () => {
        navigateToNextStep?.()
      },
      onError: (error) => {
        setSubmitError(error as GrapeAPIErrorType)
      },
    },
  })

  const deleteMutation = deleteEnrollment(currentPlan?.id || '')

  const onSelectPlan = (plan: VisionPlan | DentalPlan) => {
    createEnrollment.mutate({
      enrollmentEventId,
      data: {
        plan_code: plan.plan_code,
        carrier_name: plan.carrier,
        state: 'in_member_cart',
        covers_medical: false,
        covers_dental: false,
        covers_vision: true,
        quoted_premium: Number(plan.premium.subunits / 100),
      },
    })
  }

  const onRemovePlan = () => {
    deleteMutation.mutate()
    navigateToNextStep?.()
  }

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error) {
    return <PageErrorBanner error={error} />
  }

  if (submitError) {
    return (
      <Banner
        title='A problem has occurred while processing your request'
        type='critical'
      >
        <OnboardingGenericErrorMessage error={submitError} />
      </Banner>
    )
  }

  function renderButtons () {
    const isMobile = !breakpointIsAtLeast('xs')

    const backButton = (
      <Button
        size='large'
        variant='secondary'
        onClick={() => navigateToPreviousStep?.(onboardingData)}
      >
        Back
      </Button>
    )

    const removeButton = (
      <Button
        data-testid='remove-plan-button'
        size='large'
        variant='tertiary-deemphasized'
        onClick={() => onRemovePlan()}
      >
        I don't want a vision plan
      </Button>
    )

    const continueButton = (
      <Button
        size='large'
        variant='primary'
        rightIcon={ArrowRightIcon}
        onClick={() => navigateToNextStep()}
      >
        Continue
      </Button>
    )

    const skipButton = (
      <Button
        variant='tertiary-deemphasized'
        onClick={() => navigateToNextStep()}
        size='large'
      >
        Skip vision
      </Button>
    )

    if (currentPlan) {
      return isMobile
        ? (
          <ButtonGroup layout='vertical' size='large'>
            {continueButton}
            {backButton}
            {removeButton}
          </ButtonGroup>
          )
        : (
          <div className='flex items-center justify-between'>
            {backButton}
            <ButtonGroup
              size='large'
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              {removeButton}
              {continueButton}
            </ButtonGroup>
          </div>
          )
    }

    return isMobile
      ? (
        <ButtonGroup size='large' layout='vertical'>
          {backButton}
          {skipButton}
        </ButtonGroup>
        )
      : (
        <div className='flex items-center justify-between gap-2'>
          {backButton}
          {skipButton}
        </div>
        )
  }

  return (
    <div data-testid='VisionPlansPage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='text-2xl font-semibold leading-9 tracking-normal text-gray-800 mb-3'>
          Choose your vision insurance
        </h1>
      </div>
      {availablePlans.length > 0 && (
        <div className='flex flex-col mb-10 gap-y-6'>
          {renewalPlan && (
            <div>
              <VisionPlanCard
                badgeCategory='renewal'
                key={`${renewalPlan.carrier}-${renewalPlan.name}`}
                displayedPlan={renewalPlan}
                onSelectPlan={onSelectPlan}
                currentPlan={currentPlan}
              />
            </div>
          )}
          {availablePlans.map((plan) => {
            if (plan.plan_code === renewalPlanCode) {
              return null
            }
            return (
              <VisionPlanCard
                key={`${plan.carrier}-${plan.name}`}
                displayedPlan={plan}
                onSelectPlan={onSelectPlan}
                currentPlan={currentPlan}
              />
            )
          })}
        </div>
      )}
      {renderButtons()}
    </div>
  )
}
